import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Loader, NoItemsYet, SimpleModal } from 'components';
import { CalendarInterval } from './common';
import { apptEnums, CALENDAR_DAY_LAYOUT, DEFAULT_CALENDAR_INTERVAL, findFromList, getBorderColorAndText, getCalendarGrid } from './constants';
import { FindLoad, Images, useModal, User, useSecondaryModal } from 'utils';
import { checkCreateApptPermissions, getDisplayFromFullType, isUnassignedAppt, SERVICE } from '../constants';
import { MoreAppts } from './modals/moreAppts';
import { CreateEvent, ScheduleDetails } from './modals';
import { availabilityScheduleActions } from 'store';
import CalendarCustomView from './calendarCustomView';
import { ScheduleInformation } from './scheduleInformation';
import { SelectedStaffs } from './common/selectedStaffs';
import { useApptReschedule } from './useApptReschedule';
import { CalendarEvent } from './calendarEvent';

const DragAndDropCalendar = withDragAndDrop(Calendar);
const ACTION_TYPE = 'GET_APPOINTMENT';

export const Selectable = ({}) => {
    const { appointments, clientSchedule, staffSchedule } = useSelector((state) => ({
        appointments: state.appointment.appointments,
        adminsList: state.admins.adminsList,
        clientSchedule: state.availabilitySchedule.clientSchedule,
        staffSchedule: state.availabilitySchedule.staffSchedule,
    }));
    moment.locale('en', {
        week: {
            dow: 0,
        },
    });
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const localizer = momentLocalizer(moment);
    const currentDate = moment().utc();
    const currentWeekStart = currentDate.clone().startOf('week');
    const currentWeekEnd = currentDate.clone().endOf('week');
    const date1 = moment.utc(info?.firstDate ? info.firstDate : currentWeekStart);
    const date2 = moment.utc(info?.lastDate ? info.lastDate : currentWeekEnd);
    const differenceInDays = date2.diff(date1, 'days') + 1;
    const [appointmentById, setAppointmentById] = useState(null);
    const [date, setDate] = useState(null);
    const { open } = useModal();
    const { userInfo } = User();
    const { openSecondary } = useSecondaryModal();
    const loader = FindLoad(ACTION_TYPE);
    let userSettings = null;
    try { userSettings = JSON.parse(localStorage.getItem('userSettings')); } catch (e) { /* corrupt data */ }
    const dispatch = useDispatch();
    const { handleMove, applyPendingMove, canDrag } = useApptReschedule(appointments);

    useEffect(() => {
        if (info?.clients?.length === 1) {
            dispatch(availabilityScheduleActions.getClientSchedule(info?.clients[0]));
        } else {
            dispatch(availabilityScheduleActions.removeClientSchedule());
        }
        if (info?.staffs?.length === 1) {
            dispatch(availabilityScheduleActions.getStaffSchedule(info?.staffs[0]));
        } else {
            dispatch(availabilityScheduleActions.removeStaffSchedule());
        }
    }, [info]);
    const handleOpenClose = (date) => {
        setDate(date ? date : null);
    };

    const handleReturnColor = (i) => {
        const { color: borderColor, background, border, textColor } = getBorderColorAndText(
            i?.status,
            i?.type,
            i?.issues,
            isUnassignedAppt(i),
        );
        return { borderColor, background, border, textColor };
    };

    const renderTime = (evDate, time) => {
        const dateStr = evDate.slice(0, 10);
        const timeStr = time.slice(11, 16);
        return moment(`${dateStr}T${timeStr}:00`).toDate();
    };

    // const events = (info?.staff || info?.client) ?
    const events = applyPendingMove(
        info?.staffs?.length || info?.clients?.length
            ? appointments?.appts?.map((i) => {
                  return {
                      id: i?.id,
                      title: getDisplayFromFullType(i.type),
                      start: renderTime(i.startDate, i.startTime),
                      end: renderTime(i.startDate, i.endTime),
                      originalStart: i.startTime,
                      originalEnd: i.endTime,
                      borderColor: handleReturnColor(i)?.borderColor,
                      border: handleReturnColor(i)?.border,
                      background: handleReturnColor(i)?.background,
                      textColor: handleReturnColor(i)?.textColor,
                      staff: i?.staff,
                      client: i?.client,
                      clientCode: i?.type === SERVICE ? i?.clientCode : null,
                      serviceCptCode: i?.type === SERVICE ? i?.cptCode : null,
                      status: i?.status,
                      eventType: i?.type,
                      cancelReason: i?.cancelReason,
                      supervisee: i?.supervisee,
                      ...i,
                  };
              })
            : []
    );
    const handleOpenCloseModal = (date) => {
        openSecondary(<ScheduleDetails currentId={date?.id} />);
    };

    useEffect(() => {
        if (appointments && appointmentById) {
            const current = appointments?.appts?.find((i) => i?.id === appointmentById?.id);
            setAppointmentById(current);
        }
    }, [appointments]);

    const handleSelect = ({ action, start, end }) => {
        if (checkCreateApptPermissions(userInfo)) {
            const date = {
                startDate: moment(start).format('YYYY-MM-DD'),
                startTime: moment(start).format(),
                endTime: moment(end).format(),
            };
            handleOpenClose(date);
        }
    };

    const CustomToolbar = () => {
        return (
            <div>
                <CalendarInterval
                    userSettings={userSettings}
                    // handleSelect={setSlot}
                    // selected={slot}
                />
            </div>
        );
    };

    const ResetToolbar = () => {
        return <div />;
    };

    const scrollToTime = useMemo(() => new Date(1972, 0, 1, 6), []);

    const handleNavigate = (date, view, action) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        navigate('/schedule', {
            state: {
                ...info,
                firstDate: formattedDate,
                lastDate: formattedDate,
            },
        });
    };

    const renderStyles = (item) => {
        return {
            borderLeft:
                item?.status !== apptEnums.COMPLETED && item?.status !== apptEnums.CANCELLED
                    ? `4px solid ${item?.borderColor}`
                    : `1px solid ${item?.border}`,
            borderRight: `1px solid ${item?.border}`,
            borderTop: `1px solid ${item?.border}`,
            borderBottom: `1px solid ${item?.border}`,
            background: item?.background,
            borderRadius: '4px',
        };
    };

    const handleShowMore = (events, date) => {
        open(<MoreAppts appts={events} day={date} handleOpenDetail={(id) => handleOpenCloseModal({ id: id })} />);
    };

    const dayViewGrid = getCalendarGrid(DEFAULT_CALENDAR_INTERVAL);

    const slotPropGetter = useCallback(
        (date) => {
            const { step } = getCalendarGrid(info?.slot || userSettings?.slot);
            const user1Available = findFromList(date, clientSchedule, step);
            const user2Available = findFromList(date, staffSchedule, step);

            // Skip styling for unavailable slots
            if (!user1Available && !user2Available) {
                return { className: 'slotDefault' };
            }

            let color;
            let fillInfo;

            if (user1Available && user2Available) {
                // Both users are available — use intersection of their fill ranges
                color = 'rgb(218,255,212)';
                fillInfo = {
                    startPercent: Math.max(user1Available.startPercent, user2Available.startPercent),
                    endPercent: Math.min(user1Available.endPercent, user2Available.endPercent),
                };
            } else if (user1Available) {
                color = 'rgb(239, 248, 255)';
                fillInfo = user1Available;
            } else {
                color = 'rgb(249, 245, 255)';
                fillInfo = user2Available;
            }

            const isFull = fillInfo.startPercent === 0 && fillInfo.endPercent === 100;
            const styles = isFull
                ? { backgroundColor: color, color: 'black' }
                : {
                      background: `linear-gradient(to bottom, transparent ${fillInfo.startPercent}%, ${color} ${fillInfo.startPercent}%, ${color} ${fillInfo.endPercent}%, transparent ${fillInfo.endPercent}%)`,
                      color: 'black',
                  };

            return {
                className: 'slotDefault',
                style: styles,
            };
        },
        [clientSchedule, staffSchedule]
    );

    if (loader?.length) {
        return <Loader />;
    }

    return (
        <div className="drag-and-drop-calendar">
            {!info?.staffs?.length && !info?.clients?.length && (
                <NoItemsYet
                    styles={{ position: 'relative' }}
                    image={Images.noAppt}
                    text={'No data to display'}
                    subTitle={'Select a staff member or client to view appts'}
                    height={'70vh'}
                />
            )}
            <div className="full-width">
                <ScheduleInformation fromCalendar={true} />

                <div className="flex-align-start full-width">
                    {!!info?.staffFull?.length && info?.multiStaffView && <SelectedStaffs />}

                    <div
                        className="full-width"
                        style={{
                            opacity: !info?.staffs?.length && !info?.clients?.length ? '0.5' : '1',
                            pointerEvents: !info?.staffs?.length && !info?.clients?.length ? 'none' : 'auto',
                        }}
                    >
                        {differenceInDays === 0 || differenceInDays === 1 ? (
                            <DragAndDropCalendar
                                className="calendar-container"
                                date={info?.firstDate ? moment(info.firstDate).toDate() : new Date()}
                                components={{
                                    toolbar: ResetToolbar,
                                    event: CalendarEvent,
                                }}
                                defaultView={Views.DAY}
                                dayLayoutAlgorithm={CALENDAR_DAY_LAYOUT}
                                tooltipAccessor={null}
                                // Same 1-hour rows as before, split into 15-minute drag/resize/select slots
                                step={dayViewGrid.step}
                                timeslots={dayViewGrid.timeslots}
                                selectable
                                resizable
                                onEventDrop={handleMove}
                                onEventResize={handleMove}
                                draggableAccessor={canDrag}
                                resizableAccessor={canDrag}
                                localizer={localizer}
                                events={events || []}
                                startAccessor="start"
                                endAccessor="end"
                                onSelectEvent={(i) => handleOpenCloseModal(i)}
                                onSelectSlot={(e, j) => handleSelect(e, j)}
                                onNavigate={handleNavigate}
                                eventPropGetter={(myEventsList) => {
                                    return {
                                        style: renderStyles(myEventsList),
                                    };
                                }}
                                formats={{
                                    timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'h a', culture),
                                }}
                                scrollToTime={scrollToTime}
                            />
                        ) : differenceInDays > 7 ? (
                            // Month drop moves the appt to another day and keeps its time of day
                            // (the DnD addon merges the target date with the original start).
                            // No resize here: stretching in month view spans days, which an appt can't.
                            <DragAndDropCalendar
                                date={info?.firstDate ? moment(info.firstDate).toDate() : new Date()}
                                components={{
                                    toolbar: ResetToolbar,
                                    event: CalendarEvent,
                                }}
                                localizer={localizer}
                                events={events || []}
                                startAccessor="start"
                                endAccessor="end"
                                onEventDrop={handleMove}
                                draggableAccessor={canDrag}
                                resizable={false}
                                onSelectEvent={(i) => handleOpenCloseModal(i)}
                                popup
                                tooltipAccessor={null}
                                style={{ height: 800, overflow: 'auto' }}
                                onShowMore={handleShowMore}
                                onNavigate={handleNavigate}
                                eventPropGetter={(myEventsList) => {
                                    return {
                                        style: renderStyles(myEventsList),
                                    };
                                }}
                            />
                        ) : (
                            <div>
                                <CalendarCustomView
                                    events={events}
                                    info={info}
                                    components={{
                                        toolbar: CustomToolbar,
                                        event: CalendarEvent,
                                    }}
                                    onSelectEvent={(i) => handleOpenCloseModal(i)}
                                    onShowMore={handleShowMore}
                                    onNavigate={handleNavigate}
                                    onSelectSlot={(e, j) => handleSelect(e, j)}
                                    onEventDrop={handleMove}
                                    onEventResize={handleMove}
                                    draggableAccessor={canDrag}
                                    eventPropGetter={(myEventsList) => {
                                        return {
                                            style: renderStyles(myEventsList),
                                        };
                                    }}
                                    slotPropGetter={info?.clients?.length === 1 || info?.staffs?.length === 1 ? slotPropGetter : null}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SimpleModal
                handleOpenClose={() => handleOpenClose()}
                openDefault={date}
                content={<CreateEvent date={date} handleOpenClose={handleOpenClose} fromCalendar={true} />}
            />
        </div>
    );
};
